<template>
  <div class="Component-reviewer-map">
    <div id="pilotNetworkMap"></div>
    <div class="sidebar-list">
      <ReviewerMapSidebarDetail
        v-if="showDetail"
        :closeDetailPop="closeDetailPop"
        :selectedReviewer="selectedReviewer"
        :isMAACMemberCheckboxDefault="isMAACMemberCheckbox"
        :isMAACMember="isMAACMember"
        :bookingCbfn="bookingCbfn"
      />
      <div class="filter-wrap">
        <md-field>
          <md-input
            type="search"
            class="mb-3"
            clearable
            style="padding-left: 20px;"
            :placeholder="$t('Search')"
            v-model="searchQuery"
          ></md-input>
        </md-field>
        <md-checkbox
          class="maac-reviewer"
          v-model="isMAACMemberCheckbox"
          @change="isMAACMemberFilter"
          >{{ $t("MAAC Club Designated Flight Reviewer") }}</md-checkbox
        >
      </div>

      <md-list class="md-double-line">
        <md-list-item
          class="list-item"
          v-for="(item, idx) in reviewList"
          :key="idx"
          @click="flyTo(item)"
        >
          <div class="md-list-item-text">
            <div
              v-if="item.pilot_network === PILOT_NETWORK.MAAC_CLUB"
              class="star-wrapper"
            >
              <span class="star material-icons">
                star
              </span>
              {{ item.club_name }}
            </div>
            <div
              class="reviewer-block-title"
              v-if="
                item.pilot_network === PILOT_NETWORK.PUBLIC_SERVICE_REVIEWER
              "
            >
              <img src="/img/police.png" alt="" />
              {{ $t("Public Service Designated Flight Reviewer") }}
            </div>
            <div
              class="reviewer-block-title"
              v-if="item.pilot_network === PILOT_NETWORK.REVIEWER"
            >
              <img src="/img/rpa-blue.png" alt="" />
              {{ $t("Independent Flight Reviewer") }}
            </div>
            <div :style="`margin-top: 8px; font-size: 16px; color:#333; font-weight: ${[PILOT_NETWORK.MAAC_CLUB, PILOT_NETWORK.PUBLIC_SERVICE_REVIEWER, PILOT_NETWORK.REVIEWER].indexOf(item.pilot_network) > -1 ? 400 : 600}`">
              {{
                item.review_street
                  ? item.review_street
                  : `${item.latitude &&
                      item.latitude.toFixed(6)}, ${item.longitude &&
                      item.longitude.toFixed(6)}`
              }}, {{ item.review_city }},
              {{ item.review_province }}
            </div>
            <div
              v-if="item.review_street_desc"
              class="sub-title"
              style="margin-top: 8px;"
            >
              {{ item.review_street_desc }}
            </div>
          </div>
          <md-tooltip
            v-if="item.pilot_network === PILOT_NETWORK.MAAC_CLUB"
            md-direction="top"
            style="text-align: left;"
          >
            <div style="color: #000; font-weight: 500;">
              {{ $t("Preferred flight review site:") }}
            </div>
            <div>
              •⁠
              {{
                $t(
                  "Includes a free 3-month MAAC introductory membership paid for by us."
                )
              }}
            </div>
            <div>
              •⁠
              {{
                $t("Once a member, the flight review is covered by insurance.")
              }}
            </div>
          </md-tooltip>

          <div class="md-list-action">
            <div class="material-icons">
              arrow_forward_ios
            </div>
          </div>
        </md-list-item>
      </md-list>
    </div>
    <Notifications />
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "mapbox-gl/dist/mapbox-gl.css";
import { mapboxAccessToken, PILOT_NETWORK } from "@/const";
import {
  getReviewerList,
  checkUserRoles
} from "@/services/flightReview.service.js";
import { coordinatesGeocoder } from "@/helpers/utils";
import { mapFields } from "vuex-map-fields";
import swal from "sweetalert2";
import ReviewerMapSidebarDetail from "./ReviewerMapSidebarDetail.vue";

let MAP_MARKERS = []
export default {
  name: "PilotNetwork",
  components: {
    ReviewerMapSidebarDetail
  },
  props: {
    bookingCbfn: {
      type: Function,
      default: () => {}
    },
    reviewerNumberCbfn: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      latitude: 43.4723,
      longitude: -80.5449,
      selectedReviewer: {},
      showDetail: false,
      searchQuery: "",
      reviewList: [],
      reviewFullList: [],
      clubReviewList: [],
      clubReviewListLoading: false,
      PILOT_NETWORK,
      isMAACMember: false,
      isMAACMemberCheckbox: false,
    };
  },
  computed: {
    ...mapFields("auth", ["loggedOn"]),
    ...mapFields("flightReview", ["booking"])
  },
  async mounted() {
    if (this.$route.path !== "/flight-review/map") {
      const { member_flag } = await checkUserRoles();
      this.isMAACMember = member_flag;
      
      // keep maac checkbox off, if user have selected
      if (!this.booking.reviewerId && this.isMAACMember) {
        this.isMAACMemberCheckbox = true;
      }
    }

    this.loadMap();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        this.longitude = pos.coords.longitude;
        this.latitude = pos.coords.latitude;
        this.map.flyTo({
          center: [this.longitude, this.latitude]
        });
      });
    }
  },
  unmounted() {
    MAP_MARKERS = []
  },
  methods: {
    async loadMap() {
      const self = this;
      mapboxgl.accessToken = mapboxAccessToken;
      this.map = new mapboxgl.Map({
        container: "pilotNetworkMap",
        style: "mapbox://styles/mapbox/satellite-streets-v11",
        center: [this.longitude, this.latitude],
        zoom: 10
      });

      const nav = new mapboxgl.NavigationControl({
        showCompass: false
      });
      this.map.addControl(nav, "top-left");

      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        localGeocoder: coordinatesGeocoder,
        countries: "ca",
        marker: false,
        mapboxgl: mapboxgl,
        language:
          window.localStorage.getItem("locale") === "fr" ? "fr-CA" : "en-US",
        placeholder: this.$t("Address")
      });
      this.map.addControl(geocoder);

      await this.getReviewers();
      this.reviewerNumberCbfn &&
        this.reviewerNumberCbfn(this.reviewList.length);
      this.reviewList.forEach(item => {
        if (
          this.booking.addressId === item._id.$oid
        ) {
          this.selectedReviewer = item;
          this.map.flyTo({
            center: [item.longitude, item.latitude],
            zoom: 15,
            essential: true // for animation smooth
          });
          this.showDetail = true;
        }
      });

      // after zoom-center-transition action reload map
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 200);
    },
    renderMaker(item, self) {
      const customMarker = document.createElement("div");
      customMarker.className =
        item.pilot_network === PILOT_NETWORK.MAAC_CLUB
          ? "custom-marker maac-club"
          : item.pilot_network === PILOT_NETWORK.PUBLIC_SERVICE_REVIEWER
            ? "custom-marker public-service"
            : "custom-marker";
      if (item.longitude && item.latitude) {
        const marker = new mapboxgl.Marker(customMarker)
          .setLngLat([item.longitude, item.latitude])
          .addTo(this.map);
        MAP_MARKERS.push(marker)
        if (item.pilot_network === PILOT_NETWORK.MAAC_CLUB) {
          marker.setPopup(
            new mapboxgl.Popup({ closeButton: false }).setHTML(`
              <div style="color: #000; font-weight: 500;">${this.$t(
              "Preferred flight review site:"
            )}</div>
              <div>•⁠ ${this.$t(
              "Includes a free 3-month MAAC introductory membership paid for by us."
            )}</div>
              <div>•⁠ ${this.$t(
              "Once a member, the flight review is covered by insurance."
            )}</div>
            `)
          );
        }

        if (item.pilot_network === PILOT_NETWORK.PUBLIC_SERVICE_REVIEWER) {
          marker.setPopup(
            new mapboxgl.Popup({ closeButton: false }).setHTML(`
              <div style="color: #000; font-weight: 500;">${this.$t(
              "Public Service Designated Flight Reviewer"
            )}</div>
            `)
          );
        }

        if (item.pilot_network === PILOT_NETWORK.REVIEWER) {
          marker.setPopup(
            new mapboxgl.Popup({ closeButton: false }).setHTML(`
              <div style="color: #000; font-weight: 500;">${this.$t(
              "Independent Flight Reviewer"
            )}</div>
            `)
          );
        }

        customMarker.addEventListener("mouseenter", () =>
          marker.togglePopup()
        );
        customMarker.addEventListener("mouseleave", () =>
          marker.togglePopup()
        );
      }

      customMarker.addEventListener("click", function () {
        self.flyTo(item);
      });
    },
    flyTo(item) {
      if (!item.longitude || !item.latitude) {
        this.$notify({
          message: this.$t("Reviewer address does not exist."),
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "warning",
          timeout: 3000
        });
        return;
      }
      this.selectedReviewer = item;
      this.map.flyTo({
        center: [item.longitude, item.latitude],
        zoom: 15,
        essential: true // for animation smooth
      });
      this.showDetail = true;
    },
    closeDetailPop() {
      this.selectedReviewer = {};
      if (this.bookingCbfn) this.bookingCbfn({});
      this.showDetail = false;
      this.map.setZoom(10);
    },
    async getReviewers(keywords) {
      const { data } = await getReviewerList({
        reviewer_type: this.isMAACMemberCheckbox ? "maac_reviewer" : undefined,
        isLogin: this.$route.path === "/flight-review/map" ? false : true
      });
      let reviewList = JSON.parse(data.data);
      reviewList = reviewList.sort((a, b) => {
        // sort by review_province
        if (a.review_province.toLowerCase() < b.review_province.toLowerCase())
          return -1;
        if (a.review_province.toLowerCase() > b.review_province.toLowerCase())
          return 1;
        // sort by review_city
        if (a.review_city.toLowerCase() < b.review_city.toLowerCase())
          return -1;
        if (a.review_city.toLowerCase() > b.review_city.toLowerCase()) return 1;
        return 0;
      });
      this.reviewFullList = reviewList;
      this.reviewList = reviewList;
      this.getReviewersSearch(keywords);
    },
    getReviewersSearch(keywords = "") {
      const lowerCaseKeyword = keywords.toLowerCase();
      const reviewList = [...this.reviewFullList].filter(
        item =>
          !!(
            item.review_province &&
            item.review_province.toLowerCase().includes(lowerCaseKeyword)
          ) ||
          !!(
            item.review_city &&
            item.review_city.toLowerCase().includes(lowerCaseKeyword)
          ) ||
          !!(
            item.review_street &&
            item.review_street.toLowerCase().includes(lowerCaseKeyword)
          ) ||
          !!(
            item.review_postalcode &&
            item.review_postalcode.toLowerCase().includes(lowerCaseKeyword)
          ) ||
          !!(
            item.club_name &&
            item.club_name.toLowerCase().includes(lowerCaseKeyword)
          )
      );

      this.reviewList = reviewList;
    },
    isMAACMemberFilter(val) {
      this.isMAACMemberCheckbox = val;
      this.getReviewers(this.searchQuery);
    }
  },
  watch: {
    searchQuery(value) {
      this.getReviewersSearch(value);
    },
    reviewList(list) {
      MAP_MARKERS.forEach((marker) => marker.remove())
      MAP_MARKERS = []
      list.forEach(item => {
        this.renderMaker(item, this)
      })
    }
  }
};
</script>

<style lang="scss">
.Component-reviewer-map {
  display: flex;
  height: 60vh;

  #pilotNetworkMap {
    height: 100%;
    flex: 8;
    margin: 0px;
    padding: 0px;
  }

  flex-shrink: 0;

  .sidebar-list {
    position: relative;
    flex: 4;
    padding-left: 16px;
    overflow: auto;

    .md-double-line {
      overflow: auto;
      height: calc(100% - 60px);
    }

    .detail-pop {
      position: absolute;
      left: 0;
      width: 100%;
      top: 0;
      bottom: 0;
      z-index: 10;
      background: #fff;
      padding-left: 16px;

      .close-con {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 10px;
        font-weight: 600;
      }
    }
  }
  @media (max-width: 768px) {
    #pilotNetworkMap {
      display: none;
    }

    .sidebar-list {
      width: 100%;
      overflow: auto;
    }
  }

  .custom-marker {
    background-image: url("../../../../public/img/map-marker.png");
    background-size: cover;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    cursor: pointer;
    &.maac-club {
      background-image: url("../../../../public/img/map-marker-star.png");
    }
    &.public-service {
      background-image: url("../../../../public/img/map-marker-police.png");
    }
  }

  .list-item {
    cursor: pointer;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .pointer {
    cursor: pointer;
  }

  .sub-title {
    color: #999;
  }

  .star-wrapper,
  .reviewer-block-title {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-weight: 600;
    .star {
      display: inline;
      width: 30px;
      color: #df8f3a;
      margin-top: -2px;
      -webkit-text-fill-color: #df8f3a;
    }

    .maac-reviewer,
    img {
      width: 16px;
      min-width: 16px;
      margin-right: 8px;
    }
  }
  .filter-wrap {
    display: flex;
    align-items: center;
    .maac-reviewer {
      min-width: 210px;
      margin-left: 8px;
    }
  }
}
</style>
